import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import SignatureManager from "../../components/SignatureManager";
import ReviewManager from "../../components/ReviewManager";
import CompanyAddress from "../../components/CompanyAddress";

import "../../styles/Form.css";
import logo from "../../images/logo.png";

const pdf = "/policy-files/Printable-Alberta-Abstract-Consent.pdf";

// ✅ File validation helpers
const allowedTypes = ["application/pdf", "image/jpeg", "image/png"];

const validateFile = (file) => {
  if (!file) return null;

  if (!allowedTypes.includes(file.type)) {
    alert("Only PDF, JPG, or PNG files are allowed.");
    return null;
  }

  return file;
};

const AlbertaAbstractConsent = () => {
  const API_BASE = process.env.REACT_APP_API_BASE_URL;
  const [username, setUsername] = useState("");
  const userId = localStorage.getItem("userId");
  const userRole = localStorage.getItem("userRole");
  const isAdmin = userRole === "1";
  const navigate = useNavigate();
  const { id: paramId } = useParams();
  const effectiveUserId = isAdmin && paramId ? paramId : userId;

  const [isSigned, setIsSigned] = useState(false);
  const [signedDate, setSignedDate] = useState("");
  const [file, setFile] = useState(null);
  const [uploadedFileName, setUploadedFileName] = useState(null);
  const [originalFileName, setOriginalFileName] = useState(null);

  useEffect(() => {
    const fetchUserName = async () => {
      try {
        const token = localStorage.getItem("token");
        const url = isAdmin
          ? `${API_BASE}/api/users/${effectiveUserId}`
          : `${API_BASE}/api/user/profile`;

        const res = await axios.get(url, {
          headers: { Authorization: `Bearer ${token}` },
        });

        const { first_name, last_name } = res.data;
        setUsername(`${first_name} ${last_name}`);
      } catch (err) {
        console.error("Error fetching user name:", err);
      }
    };

    const fetchTaskStatus = async () => {
      try {
        const res = await axios.get(
          `${API_BASE}/api/usertasks/status/${effectiveUserId}`,
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
          }
        );

        const TASK_ID = 42;
        const idKey = String(TASK_ID);
        const nameKey = "alberta_consent";

        const status =
          res.data.status?.[idKey] ??
          res.data.status?.[nameKey] ??
          "not_completed";

        const signed = status === "review" || status === "completed";

        if (signed) {
          setIsSigned(true);
          setSignedDate(
            res.data.signdates?.[idKey] ?? res.data.signdates?.[nameKey]
          );
        }
      } catch (err) {
        console.error("Error checking task status:", err);
      }
    };

    fetchUserName();
    fetchTaskStatus();
  }, [API_BASE, userId, isAdmin, effectiveUserId]);

  useEffect(() => {
    const fetchUploaded = async () => {
      try {
        const res = await axios.get(
          `${API_BASE}/api/albertaconsent/${effectiveUserId}`,
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
          }
        );
        setUploadedFileName(res.data.file_name);
        setOriginalFileName(res.data.original_name);
      } catch (err) {
        console.log("No previous upload");
      }
    };
    fetchUploaded();
  }, [API_BASE, effectiveUserId]);

  const handleUpload = async () => {
    if (!file || !effectiveUserId) return;

    const formData = new FormData();
    formData.append("file", file);
    formData.append("userId", effectiveUserId);

    try {
      const res = await axios.post(
        `${API_BASE}/api/albertaconsent/upload?userId=${effectiveUserId}`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );
      setUploadedFileName(res.data.filename);
      setOriginalFileName(res.data.originalName);
      alert("File uploaded!");
    } catch (err) {
      console.error("Upload error:", err);
      alert("Upload failed");
    }
  };

  const handleCancel = () => {
    if (isAdmin && paramId) {
      navigate(`/userhome/${paramId}`);
    } else {
      navigate("/userhome");
    }
  };

  return (
    <>
      {/* 🔝 Header Section with Status + Return Button */}
      <div className="d-flex justify-content-between align-items-center pt-3">
        <div className="d-flex align-items-center">
          <span className="me-2 text-danger"></span>
          <span style={{ fontSize: "18px", fontWeight: "500" }}>
            Printable – Alberta Abstract Consent
          </span>
        </div>
        <button
          type="button"
          className="btn btn-secondary"
          onClick={handleCancel}
        >
          Return
        </button>
      </div>

      <div
        className="form-wrapper my-4 print-area"
        style={{ fontSize: "15px", lineHeight: "1.6" }}
      >
        {/* 🔹 Header */}
        <div className="row align-items-center mb-4">
          <div className="col-3 text-start">
            <img
              src={logo}
              alt="Company Logo"
              style={{ width: "150px", height: "auto" }}
            />
          </div>
          <div className="col-6 text-center">
            <h2 className="m-0">Printable – Alberta Abstract Consent</h2>
          </div>
          <CompanyAddress />
        </div>

        {/* 🔹 Instructions */}
        <div className="mt-4">
          <ol>
            <li>
              Click the download icon for the{" "}
              <strong>Alberta Abstract Consent</strong> form.
            </li>
            <li>
              Print the form and complete it in{" "}
              <strong>blue or black ink</strong>. If fillable, complete before
              printing for signing.
            </li>
            <li>
              Take a clear picture or scan of the completed form, and upload it
              below.
            </li>
            <li>
              Once{" "}
              <span style={{ color: "blue", fontWeight: "bold" }}>
                Upload Button
              </span>{" "}
              appears, click <strong>Upload</strong>.
            </li>
          </ol>
        </div>

        {/* 🔹 PDF Viewer */}
        <iframe
          className="mt-4"
          src={pdf}
          width="100%"
          height="800px"
          style={{ border: "none" }}
          title="PDF Viewer"
        />

        {/* 🔹 Upload Section */}
        <div className="mt-5">
          <label className="form-label">
            Upload Completed Form (PDF / JPG / PNG):
          </label>
          <input
            type="file"
            className="form-control mb-2"
            accept=".pdf,.jpg,.jpeg,.png"
            disabled={!isAdmin && isSigned}
            onChange={(e) => {
              const selected = validateFile(e.target.files[0]);
              setFile(selected);
            }}
          />
          <div className="d-flex justify-content-end">
            <button
              type="submit"
              className="btn btn-primary"
              onClick={handleUpload}
              disabled={!file || (!isAdmin && isSigned)}
            >
              Upload
            </button>
          </div>
        </div>

        {/* 🔹 Uploaded File Display */}
        {uploadedFileName && (
          <div className="mt-3 p-2 bg-light border rounded">
            Uploaded File:{" "}
            <a
              href={`${API_BASE}/uploads/albertaconsent/${uploadedFileName}`}
              target="_blank"
              rel="noopener noreferrer"
              download={originalFileName}
            >
              {originalFileName}
            </a>
          </div>
        )}

        <hr className="my-5" />

        {/* 🔹 Signature Section */}
        <form>
          <div className="row mb-3 mt-5">
            <div className="col">
              <label className="form-label">Employee Name</label>
              <input
                type="text"
                className="form-control"
                value={username}
                readOnly
              />
            </div>
            <div className="col">
              <label className="form-label">Date</label>
              <input
                type="text"
                className="form-control"
                value={
                  isSigned && signedDate
                    ? new Date(signedDate).toLocaleDateString()
                    : new Date().toLocaleDateString()
                }
                readOnly
              />
            </div>
          </div>
        </form>

        {/* 🔹 Signature + Review Managers */}
        {userId && (
          <>
            <SignatureManager
              taskName="alberta_consent"
              taskId={42}
              userId={effectiveUserId}
              isAdmin={isAdmin}
            />
            <ReviewManager
              taskName="alberta_consent"
              taskId={42}
              userId={effectiveUserId}
              isAdmin={isAdmin}
            />
          </>
        )}
      </div>
    </>
  );
};

export default AlbertaAbstractConsent;
